import { ReactElement, useMemo, useState } from "react";
import { colors, fonts, glyphs } from "../../Styles";
import { AttentionTypes } from "../../Definitions/valueTypes";
import { CreateAttentionStyles } from "../../Definitions/attentionStyles";
import { useTranslation } from "react-i18next";
  
interface AttentionCard_Props {
  attentionType: AttentionTypes,
  systemContent?: ReactElement,
  problemContent?: ReactElement,
  hover?: boolean,
  onLeftClick?: () => void
  onRightClick?: () => void
}
/**
 * AttentionCard
 * @param attentionType Defines style and header. Could be good, alert, warning or neutral.
 * @param systemContent Content to parse in top content area. 
 * @param problemContent Content to parse in bottom content area.
 * @param hover If true, the card will have opacity: "0.5" and cursor: "pointer" on onMouseEnter.
 * @param onClick Adds an onClick event to the card.
 * @returns A AttentionCard with either good, alert, warning or neutral as type.
 */
export default function AttentionCard({attentionType, systemContent, problemContent, onLeftClick, onRightClick, hover = false}: AttentionCard_Props) {
  const {t} = useTranslation();
  const [isLeftHovered, setIsLeftHovered] = useState<boolean>(false); 
  const [isRightHovered, setIsRightHovered] = useState<boolean>(false); 
  const { textColor, attentionText, attentionColor } = useMemo(() => {
    var _Style = CreateAttentionStyles(t, attentionType)
    return _Style
  }, [t, attentionType ]);

  return (
    <div style={{zIndex: "0", position: "relative", width: "100%", height: "fit-content", breakInside: "avoid", marginBottom: "10px"}}>

        <div style={{ backgroundColor: attentionColor, borderRadius: "8px 8px 0px 0px", padding: "7px", textAlign: "center", ...fonts.header, ...fonts.fontSize4, color: textColor}}>
          {attentionText}
        </div>
        
        <div style={{backgroundColor: colors.greyDarkSecondary, borderRadius: "0px 0px 8px 8px",}}>
          <div style={{
            height: "fit-content",
            borderRadius: "0px 0px 8px 8px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            border: "3px solid "+attentionColor,
            borderTop: "none",
            padding: "10px"
        }}>
          {problemContent}
          {systemContent !== undefined ? <>
            <div style={{height: "1px", backgroundColor: colors.greyDarkPrimary, width: "80%", margin: "5px 0px"}}></div>
            {systemContent}
          </> : undefined}

          </div>
      </div>
        
      
      <div  
        style={{ zIndex: "3", width: "50%", height: "100%", position: "absolute", top: "0", left: "0", cursor: hover ? "pointer" : "default" }}
        onClick={onLeftClick} 
        onMouseEnter={() => hover&&setIsLeftHovered(true)}
        onMouseLeave={() => hover&&setIsLeftHovered(false)}
      >
        <div style={{ zIndex: "1", width: "100%", height: "100%", position: "absolute", top: "0", left: "0", backgroundColor: colors.greyLightTertiary, opacity: hover && isLeftHovered ? "0.8" : hover && isRightHovered ? "0.3" : "0", transition: "opacity 0.3s ease", border: "1px solid "+colors.greyLightSecondary, boxSizing: "border-box", borderRadius: "8px 0px 0px 8px" }} />
        <div style={{ position: "absolute", zIndex: "2", top: "50%", left: "50%", transform: "translate(-50%, -50%)", opacity: isLeftHovered ? "1" : "0", transition: "opacity 0.3s ease", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "3px" }}><glyphs.Search size="25px" fillColor={colors.greyDarkPrimary} /><p style={{...fonts.fontSize4, ...fonts.header, color: colors.greyDarkPrimary}}>{t("systemControls.general.inspect", {defaultValue: "inspect"})}</p></div>
      </div>

      <div  
        style={{ zIndex: "3", width: "50%", height: "100%", position: "absolute", top: "0", right: "0", cursor: hover ? "pointer" : "default" }}
        onClick={onRightClick} 
        onMouseEnter={() => hover&&setIsRightHovered(true)}
        onMouseLeave={() => hover&&setIsRightHovered(false)}
      >
        <div style={{ zIndex: "1", width: "100%", height: "100%", position: "absolute", top: "0", right: "0", backgroundColor: colors.greyLightTertiary, opacity: hover && isRightHovered ? "0.8" : hover && isLeftHovered ? "0.3" : "0", transition: "opacity 0.3s ease", border: "1px solid "+colors.greyLightSecondary, boxSizing: "border-box", borderRadius: "0px 8px 8px 0px" }} />
        <div style={{ position: "absolute", zIndex: "2", top: "50%", right: "50%", transform: "translate(50%, -50%)", opacity: isRightHovered ? "1" : "0", transition: "opacity 0.3s ease", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "3px" }}><glyphs.Close size="25px" fillColor={colors.systemRed} /><p style={{...fonts.fontSize4, ...fonts.header, color: colors.greyDarkPrimary}}>{t("systemControls.general.delete", {defaultValue: "delete"})}</p></div>
      </div>      
    </div>
  )
}